import React from 'react';
import clsx from 'clsx';
import Tooltip from '@srm/ui/tooltip';
import { MdBlock, MdCheckCircle, MdError, MdFlag } from 'react-icons/md';
import { IOffer } from '@models/winnerSelection/ITableDemand';
import { formatPrice } from '@utils/helpers';

interface ISettings {
    blackListHint?: string | null;
}

interface IProps {
    offer: IOffer;
    settings: ISettings;
}

/**
 * Упрощённая шапка колонки оффера для KO-таблицы.
 *
 * Отличия от Demand-OfferHeader:
 * - нет блока stat'ов (Цена с delta + Рейтинг) — только цена ниже pill'ов;
 * - нет result-pill (KO не имеет понятия Result);
 * - rank-лейбл показывается всегда (включая «Место 4», в отличие от Demand
 *   где для rank≥4 лейбл скрывается);
 * - нет positionCurrency / minPrice / maxSummary props.
 */
const rankLabelKO = (rank: number | string): string => {
    const n = typeof rank === 'string' ? parseInt(rank, 10) : rank;
    if (n === 1) return 'Лидер';
    if (n === 2) return 'Второй';
    if (n === 3) return 'Третий';
    return Number.isFinite(n) ? `Место ${n}` : '';
};

export const OfferHeaderKO: React.FC<IProps> = ({ offer, settings }) => {
    const rankNum = offer.rank != null && offer.rank !== '' ? Number(offer.rank) : null;
    const rankClass = rankNum != null && rankNum >= 1 && rankNum <= 3 ? `ws-offer-head__rank--${rankNum}` : '';
    const isAlternative = offer.type?.trim().toLowerCase().startsWith('альтернатив') ?? false;
    const typeName = offer.type?.toLowerCase() ?? '';

    return (
        <div className="ws-offer-head">
            <div className="ws-offer-head__top">
                <div className={clsx('ws-offer-head__rank', rankClass)}>
                    <span className="ws-offer-head__rank-num">{rankNum ?? '—'}</span>
                    {rankNum && <span className="ws-offer-head__rank-label">{rankLabelKO(rankNum)}</span>}
                    {offer.approveToStageTwo === 1 ? (
                        <Tooltip openDelay={100} background="black" position="bottom" label="Допущен ко 2 этапу">
                            <span className="ws-offer-head__approve ws-offer-head__approve--ok">
                                <MdCheckCircle size="16px" /> 2 эт.
                            </span>
                        </Tooltip>
                    ) : offer.approveToStageTwo === 0 ? (
                        <Tooltip openDelay={100} background="black" position="bottom" label="Не допущен ко 2 этапу">
                            <span className="ws-offer-head__approve ws-offer-head__approve--no">
                                <MdError size="16px" /> Отказ
                            </span>
                        </Tooltip>
                    ) : null}
                </div>
                {/* Тип предложения — тихая двухстрочная метка в правом верхнем углу
                    (как в Demand-шапке): «предложение №N» + слово типа с точкой. */}
                <Tooltip
                    openDelay={150}
                    background="black"
                    position="bottom"
                    label={`Тип предложения поставщика: ${typeName || '—'}`}
                >
                    <span className="ws-offer-head__type">
                        <span className="ws-offer-head__type-id">предложение №{offer.number}</span>
                        <span
                            className={clsx('ws-offer-head__type-name', {
                                'ws-offer-head__type-name--alt': isAlternative,
                            })}
                        >
                            <span className="ws-offer-head__type-dot" aria-hidden />
                            {typeName}
                        </span>
                    </span>
                </Tooltip>
                {/* Отдельной кнопки действий нет: кликабельна вся шапка колонки
                    (кнопка меню во всю шапку); подсказка — курсор-рука и кольцо при hover. */}
            </div>

            <div className="ws-offer-head__name">
                {offer.isInBlackList && settings.blackListHint ? (
                    <Tooltip openDelay={100} background="black" position="bottom" label={settings.blackListHint}>
                        <span className="ws-offer-head__blacklist">
                            <MdFlag size="13px" />
                        </span>
                    </Tooltip>
                ) : null}
                <span className="ws-offer-head__name-text" title={offer.name}>
                    {offer.name}
                </span>
            </div>

            {offer.isWithdrawn ? (
                <div className="ws-offer-head__sub">
                    <span className="ws-pill ws-pill--withdrawn">
                        <MdBlock size="13px" aria-hidden />
                        Отозвано
                    </span>
                </div>
            ) : null}

            {offer.price != null && (
                <div className="ws-offer-head__price-row">
                    <span className="ws-offer-head__price-label">Цена</span>
                    <Tooltip
                        openDelay={100}
                        background="black"
                        position="bottom"
                        label={`Цена предложения\nВалюта: ${offer.currencyText ?? ''} (${offer.currencyCode})`}
                    >
                        <span className="ws-offer-head__price-value">
                            {formatPrice(offer.price, offer.currencyCode)}
                        </span>
                    </Tooltip>
                </div>
            )}
        </div>
    );
};
